import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';
import {
  setCurrentPost,
  setLoading,
  setError,
  updatePostInteraction as updatePostInteractionAction,
} from '../slices/postSlice';

/**
 * Fetch a single post by ID
 */
export const fetchPostById = createAsyncThunk(
  'post/fetchById',
  async (postId: string, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/posts/${postId}`);
      dispatch(setCurrentPost(response.data.post));
      return response.data.post;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch post';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * Toggle like on the current post
 */
export const togglePostLike = createAsyncThunk(
  'post/toggleLike',
  async (postId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`/posts/${postId}/like`);
      dispatch(updatePostInteractionAction({
        liked: response.data.liked,
        disliked: response.data.disliked,
      }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to toggle like';
      return rejectWithValue(message);
    }
  }
);

/**
 * Toggle dislike on the current post
 */
export const togglePostDislike = createAsyncThunk(
  'post/toggleDislike',
  async (postId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`/posts/${postId}/dislike`);
      dispatch(updatePostInteractionAction({
        liked: response.data.liked,
        disliked: response.data.disliked,
      }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to toggle dislike';
      return rejectWithValue(message);
    }
  }
);

/**
 * Prepare post spotlight transaction
 * Validates post, calculates pricing, checks balance
 * Returns transaction payload for signing
 */
export const preparePostSpotlight = createAsyncThunk(
  'post/prepareSpotlight',
  async (
    { postId, durationHours }: { postId: string; durationHours: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/dashboard/spotlight/post/prepare', {
        postId,
        durationHours,
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to prepare spotlight';
      return rejectWithValue(message);
    }
  }
);

/**
 * Execute post spotlight for email users (backend handles transaction signing)
 */
export const executePostSpotlightEmailUser = createAsyncThunk(
  'post/executeSpotlightEmailUser',
  async (
    { postId, durationHours }: { postId: string; durationHours: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/dashboard/spotlight/post/execute-email', {
        postId,
        durationHours,
      });
      return response.data;
    } catch (error: any) {
      // Check if error response contains pricing data (insufficient funds case)
      if (error.response?.data?.pricing && error.response?.data?.balance) {
        // Return the full error response data including pricing
        return rejectWithValue(error.response.data);
      }
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to execute spotlight';
      return rejectWithValue(message);
    }
  }
);

/**
 * Record post spotlight purchase after transaction is signed
 * Saves the spotlight promotion to the database
 */
export const recordPostSpotlight = createAsyncThunk(
  'post/recordSpotlight',
  async (
    {
      postId,
      durationHours,
      txHash,
      locationId,
    }: {
      postId: string;
      durationHours: number;
      txHash: string;
      locationId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/dashboard/spotlight/purchase', {
        spotlightType: 'post',
        contentId: postId,
        locationId: locationId || null,
        duration: durationHours,
        tokenId: 'KLYX',
        txHash,
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to record spotlight';
      return rejectWithValue(message);
    }
  }
);
